"use client";

import { CodeXmlIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EyeIcon } from "@/components/ui/eye";
import { cn } from "@/lib/utils";

export type HtmlIntakeChoice = "code" | "rendered";

type HtmlIntakeDialogProps = {
  /** Filename being asked about, or null when the dialog is closed. */
  filename: string | null;
  /** Code intake needs the Claude code execution container. */
  codeAvailable: boolean;
  /** Called with the choice, or null when the user cancels (drops the attachment). */
  onChoose: (choice: HtmlIntakeChoice | null) => void;
};

function IntakeOption({
  icon,
  title,
  description,
  disabled,
  onSelect,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left transition-colors",
        "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background",
      )}
    >
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <span className="min-w-0 space-y-1">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-sm text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

export function HtmlIntakeDialog({ filename, codeAvailable, onChoose }: HtmlIntakeDialogProps) {
  return (
    <Dialog
      open={filename !== null}
      onOpenChange={(open) => {
        if (!open) onChoose(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>How should this HTML file be read?</DialogTitle>
          <DialogDescription className="break-all">{filename}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <IntakeOption
            icon={<CodeXmlIcon className="size-5" />}
            title="As code"
            description={
              codeAvailable
                ? "Upload the original file to the code execution sandbox to inspect or edit the markup."
                : "Needs a Claude model — switch models to attach HTML as code."
            }
            disabled={!codeAvailable}
            onSelect={() => onChoose("code")}
          />
          <IntakeOption
            icon={<EyeIcon size={20} />}
            title="As a rendered page"
            description="Convert it to a PDF so the AI reads the page as it looks, not its source."
            onSelect={() => onChoose("rendered")}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onChoose(null)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
